/* Created by Interactive 63 - https://interactive63.com */
/**
 * The top bar: home button, import, open and save, editable project name, save status, undo and redo,
 * transport, the theme toggle, scene settings, preview, help and export.
 * Undo and redo reflect the store's history and show the label of the change they would affect.
 */
import type { OpenableDialog } from '../dialogs/lazy-dialogs';
import type { PreviewDialog } from '../dialogs/preview-dialog';
import type { SceneSettingsDialog } from '../dialogs/scene-settings';
import type { ImportController } from '../import-controller';
import type { SaveStatus } from '../persistence/autosave';
import type { ProjectSession } from '../persistence/session';
import type { PlaybackController } from '../playback';
import { Transport } from './transport';
import { PRODUCT_NAME, STUDIO_NAME, STUDIO_URL } from '../../core/branding';
import { renameProject } from '../store/actions';
import { deleteSelectedLayers, duplicateSelectedLayers } from '../store/layer-actions';
import { addTextAndEdit } from '../store/text-actions';
import { Component } from '../ui/component';
import { announce } from '../ui/announcer';
import { h } from '../ui/dom';
import { createMenuButton, type MenuEntry } from '../ui/menu-button';
import { studioLogo } from '../ui/studio-logo';
import { toast } from '../ui/toast';
import { createAppearanceMenu } from './appearance-menu';

export interface TopbarDialogs {
  scene: SceneSettingsDialog;
  preview: PreviewDialog;
  exporter: OpenableDialog;
}

export interface TopbarProps {
  dialogs: TopbarDialogs;
  importer: ImportController;
  playback: PlaybackController;
  session: ProjectSession;
  help: HTMLElement;
}

export class Topbar extends Component<TopbarProps> {
  /** Creates the header element. */
  protected override createRoot(): HTMLElement {
    return h('header', { class: 'topbar' });
  }

  /** Builds the buttons and keeps undo, redo and the project name in step with the store. */
  protected override init(): void {
    const undo = h('button', { type: 'button', disabled: true }, 'Undo');
    const redo = h('button', { type: 'button', disabled: true }, 'Redo');
    const name = h('button', { type: 'button', class: 'project-name', title: 'Rename project' });

    undo.addEventListener('click', () => {
      this.store.undo();
    });
    redo.addEventListener('click', () => {
      this.store.redo();
    });
    name.addEventListener('click', () => {
      this.editName(name);
    });

    // The visible text changes often, so only calmer, rate-limited updates reach screen readers.
    const status = h('span', { class: 'save-status' });
    const statusText: Record<SaveStatus, string> = {
      idle: '',
      saving: 'Saving…',
      saved: 'Saved',
      unsaved: 'Not saved',
    };
    this.props.session.autosaver.onStatus((value) => {
      status.textContent = statusText[value];
      status.dataset['status'] = value;
      if (value === 'saved' || value === 'unsaved') {
        announce(statusText[value], { key: 'autosave', minIntervalMs: 10_000 });
      }
    });

    this.el.append(
      h(
        'h1',
        null,
        h(
          'button',
          {
            type: 'button',
            class: 'home-button',
            title: 'Back to the start screen',
            onClick: () => void this.props.session.goHome(),
          },
          PRODUCT_NAME,
        ),
      ),
      h(
        'a',
        {
          class: 'studio-credit',
          href: STUDIO_URL,
          target: '_blank',
          rel: 'noopener',
          title: `Made by ${STUDIO_NAME}`,
        },
        studioLogo(),
        h('span', { class: 'studio-credit-text' }, `by ${STUDIO_NAME}`),
      ),
      createMenuButton('File', this.fileEntries()),
      createMenuButton('Edit', this.editEntries()),
      name,
      status,
      undo,
      redo,
    );
    new Transport(this.store, this.props.playback).mount(this.el);
    this.el.append(
      h('div', { class: 'topbar-spacer' }),
      createAppearanceMenu(),
      h(
        'button',
        {
          type: 'button',
          title: 'Scene settings: timing, repeat, trigger and reduced motion',
          onClick: () => {
            this.props.dialogs.scene.open();
          },
        },
        'Scene',
      ),
      h(
        'button',
        {
          type: 'button',
          title: 'Preview the real export',
          onClick: () => {
            this.props.dialogs.preview.open();
          },
        },
        'Preview',
      ),
      this.props.help,
      h(
        'button',
        {
          type: 'button',
          class: 'primary',
          title: 'Export (Ctrl + E)',
          onClick: () => {
            this.props.dialogs.exporter.open();
          },
        },
        'Export',
      ),
    );

    this.select(
      (s) => s.project.name,
      (value) => {
        name.textContent = value;
      },
      { immediate: true },
    );
    // The selector reads history, which changes whenever the project does.
    this.select(
      () => [this.store.undoLabel(), this.store.redoLabel()] as const,
      ([undoLabel, redoLabel]) => {
        undo.disabled = undoLabel === undefined;
        redo.disabled = redoLabel === undefined;
        undo.title = undoLabel === undefined ? 'Undo' : `Undo ${undoLabel}`;
        redo.title = redoLabel === undefined ? 'Redo' : `Redo ${redoLabel}`;
      },
      { equality: 'shallow', immediate: true },
    );
  }

  /** The File menu: starting, opening, saving. */
  private fileEntries(): MenuEntry[] {
    const { session } = this.props;
    return [
      {
        label: 'New project',
        onSelect: () => void session.goHome(),
      },
      {
        label: 'Open project…',
        shortcut: 'Ctrl + O',
        onSelect: () => {
          session.chooseProjectFile();
        },
      },
      'separator',
      { label: 'Save', shortcut: 'Ctrl + S', onSelect: () => void session.saveProjectFile() },
      {
        label: 'Save as…',
        shortcut: 'Ctrl + Shift + S',
        onSelect: () => void session.promptSaveAs(),
      },
    ];
  }

  /** The Edit menu: history, and adding or deleting layers. */
  private editEntries(): MenuEntry[] {
    const { importer } = this.props;
    return [
      {
        label: 'Undo',
        shortcut: 'Ctrl + Z',
        disabled: () => !this.store.canUndo(),
        onSelect: () => {
          this.store.undo();
        },
      },
      {
        label: 'Redo',
        shortcut: 'Ctrl + Y',
        disabled: () => !this.store.canRedo(),
        onSelect: () => {
          this.store.redo();
        },
      },
      'separator',
      {
        label: 'Add SVG layer…',
        onSelect: () => {
          importer.chooseLayerFile();
        },
      },
      {
        label: 'Add text',
        shortcut: 'T',
        onSelect: () => {
          addTextAndEdit(this.store);
        },
      },
      {
        label: 'Duplicate layer',
        shortcut: 'Ctrl + D',
        disabled: () => this.store.getState().editor.selection.elementIds.length === 0,
        onSelect: () => {
          duplicateSelectedLayers(this.store);
        },
      },
      {
        label: 'Delete layer',
        shortcut: 'Delete',
        disabled: () => this.store.getState().editor.selection.elementIds.length === 0,
        onSelect: () => {
          const count = deleteSelectedLayers(this.store);
          if (count > 0) toast.info('Layer deleted. Undo brings it back.');
        },
      },
    ];
  }

  /** Swaps the project name button for a text field; Enter saves and Escape cancels. */
  private editName(button: HTMLElement): void {
    const input = h('input', {
      class: 'project-name-input',
      value: this.store.getState().project.name,
      'aria-label': 'Project name',
    });
    button.replaceWith(input);
    input.focus();
    input.select();
    let done = false;
    /** Restores the button, saving the typed name when requested. */
    const finish = (save: boolean): void => {
      if (done) return;
      done = true;
      input.replaceWith(button);
      if (save) renameProject(this.store, input.value);
    };
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') finish(true);
      else if (event.key === 'Escape') finish(false);
    });
    input.addEventListener('blur', () => {
      finish(true);
    });
  }
}
